import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { dashboardActions } from 'features/Dashboard/dashboardActions'
import { Locations } from 'shared/Router/Locations'
import { useNavigate } from 'react-router-dom'
import { mapIntelligenceActions } from 'redux/mapIntelligence/mapIntelligenceSlice'
import { isOnboarding } from 'features/Onboarding/helperFunctions'
import AsyncItemsView from 'features/Onboarding/dashboard/AsyncItemsView'
import styled from 'styled-components'

// New dashboard components
import AdminDashboardHeader from './components/AdminDashboardHeader'
import AtAGlanceGrid from './components/AtAGlanceGrid'
import ServicesUsedChart from './components/ServicesUsedChart'
import DashboardMap from './components/DashboardTrips/DashboardMap'
import OverviewSection from './components/OverviewSection'
import ComplianceWidget from './components/ComplianceWidget'
import AIConciergeWidget from './components/AIConciergeWidget'
import TitleButtonContainer from './components/TitleButtonContainer'

// Types and mock data
import { TimeframeOption } from './adminDashboardTypes'
import { tripPeriodForTimeframe } from './tripPeriod'
import {
  transformWorksitesToSiteData,
  mockPrograms,
  mockCompliance,
  mockAtAGlanceByTimeframe,
  mockTopServicesByTimeframe,
  mockAIConciergeByTimeframe,
} from './adminDashboardMockData'

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`

const MiddleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }
`

const MapContainer = styled.div`
  border-radius: 0.75rem;
  overflow: hidden;
  min-height: 280px;
  height: 100%;
`

const DashboardView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [selectedTimeframe, setSelectedTimeframe] =
    useState<TimeframeOption>('30d')

  const user = useSelector((state: AppState) => state.user.profile)
  const onboarding = useSelector((state: AppState) => state.onboarding)

  const { metrics, topServices, trackers } = useSelector(
    (state: AppState) => state.dashboard
  )
  const { worksites } = useSelector((state: AppState) => state.employer)
  const { allEmployeeSegmentId } = useSelector(
    (state: AppState) => state.employer
  )
  const { dashRecentTrips, worksiteGeojson } = useSelector(
    (state: AppState) => state.mapIntelligence
  )

  useEffect(() => {
    dispatch(
      mapIntelligenceActions.getBaseCommuters({
        segmentId: allEmployeeSegmentId,
      })
    )
    dispatch(dashboardActions.getDashboardMapData())
  }, [dispatch, allEmployeeSegmentId])

  useEffect(() => {
    dispatch(dashboardActions.getDashboardTrackers())
    dispatch(dashboardActions.getDashboardBulkData())
  }, [])

  // Transform real data with mock enhancements
  const siteData = transformWorksitesToSiteData(worksites)

  const recentTrips =
    dashRecentTrips?.[tripPeriodForTimeframe(selectedTimeframe)] ?? null

  // Compute modeCount for DashboardMap
  const modeCount =
    recentTrips?.servicesBreakdown?.reduce((acc, service) => {
      acc[service.modetype] = service.legs
      return acc
    }, {} as Dictionary<number>) || {}

  // Get timeframe-based data
  const atAGlanceData = mockAtAGlanceByTimeframe[selectedTimeframe]
  const servicesData = mockTopServicesByTimeframe[selectedTimeframe]
  const conciergeData = mockAIConciergeByTimeframe[selectedTimeframe]

  return (
    <>
      {isOnboarding(onboarding) ? (
        <AsyncItemsView />
      ) : (
        <Container>
          <AdminDashboardHeader
            user={user}
            selectedTimeframe={selectedTimeframe}
            onTimeframeChange={setSelectedTimeframe}
          />

          <AtAGlanceGrid
            data={atAGlanceData}
            selectedTimeframe={selectedTimeframe}
          />

          <MiddleRow>
            <TitleButtonContainer
              title='Top Services Used'
              buttonText='View Full Report'
              onClick={() => navigate(Locations.Reporting)}
              isLoading={!topServices}
            >
              <ServicesUsedChart
                services={servicesData}
                style={{ marginTop: '0.5rem' }}
              />
            </TitleButtonContainer>

            {worksiteGeojson && recentTrips?.geojson ? (
              <MapContainer>
                <DashboardMap
                  worksiteGeojson={worksiteGeojson}
                  recentTripGeojson={recentTrips.geojson}
                  activeLayer={null}
                  modeCount={modeCount}
                />
              </MapContainer>
            ) : (
              <MapContainer />
            )}
          </MiddleRow>

          <OverviewSection sites={siteData} programs={mockPrograms} />

          <BottomRow>
            <ComplianceWidget data={mockCompliance} />
            <AIConciergeWidget stats={conciergeData} />
          </BottomRow>
        </Container>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  DashboardView.displayName = 'DashboardView'
}
export default DashboardView
